import React, {useEffect, useMemo, useRef, useState} from 'react'
import styled, {css} from 'styled-components'
import {Button, ContextMenu, Icon, Li} from '@startlibs/components';
import {TextInput} from '@startlibs/form';
import {getColor} from '@startlibs/utils'
import {usePopupToggle} from '@startlibs/core';
import {CaseCoordinator} from "../../components/Sidebar";
import {jwtGetFetcher, jwtPostFetcher} from '../../utils/authFetch';
import {getJwt} from '../../hooks/useJwt';
import {setNotification} from '../../components/Notifications';
import {OPERATOR} from '../../enums/UserRole';

export const contextMenuLinkPadding = css`
  & > a {
    padding: 12px 12px 12px 19px;
  }
`;

const AssignCoordinatorHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-content: center;
    justify-content: space-between;
    align-items: center;
    padding: 16px 16px 0px 25px;
    p{
      color: #5A5A5A;
      font-weight: 600;
      font-size: 13px;
      line-height: 100%;
      margin: 0;
    }
    ${Icon}{ 
      color: #999999; 
      font-size: 20px; 
      cursor: pointer;
    }    
`

const ClearSearchButton = styled(Icon)`
  color: ${getColor('gray90')};
  font-size: 12px;
  position: absolute;
  z-index: 99;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  width: 1.5rem;
  line-height: 1.5rem;
  text-align: center;
  border-radius: 50%;
  background: rgba(0,0,0,0.1);
  :hover {
    background: #f7d3d4;
    color: ${getColor('alert')};
  }
`

const SearchIcon = styled(Icon)`
  color: #B3B3B3;
  z-index: 99;
  font-size: 18px;
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%) rotate(90deg);
  pointer-events: none;
  width: 1.25rem;
  line-height: 1.25rem;
  text-align: center;
`

const selectedCoordinatorMenuItemCss = css`
  background: ${getColor('main')};
  color: white;
  &:hover {
    background: ${getColor('main')};
  }
`;

const defaultCoordinatorMenuItemCss = css`
`;

const highlightedCoordinatorMenuItemCss = css`
  background: ${getColor('highlightColor')};
  color: white;
  &:hover {
    background: ${getColor('highlightColor')};
    color: white;
  }
`;

const NoUsersDiv = styled.div`
  padding: 8px;
  padding-top: 12px;
  color: ${getColor('gray120')};
  font-size: 13px;
  text-align: center;
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const SmileIcon = styled(Icon)`
  color: #B3B3B3;
  font-size: 16.25px;
`

const CoordinatorLink = styled.span`
  color: ${getColor('main')};
  cursor: pointer;
  ${Icon} {
    margin-left: 4px;
    font-size: 12px;
    color: ${getColor('main')};
  }
`


export const CoordinatorSelector = ({caseRequest, setCaseRequest, userInfo}) => {
  const coordinatorMenuToggle = usePopupToggle()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCoordinator, setSelectedCoordinator] = useState(null)
  const [candidateCoordinators, setCandidateCoordinators] = useState([])
  const [coordinatorError, setCoordinatorError] = useState(null)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const lastFetchedFetchKey = useRef(null)
  const listContainerRef = useRef(null)
  const itemRefs = useRef({})
  const categoriesSignature = useMemo(
    () => (caseRequest?.categories || [])
      .map(cat => cat?.id)
      .filter(Boolean)
      .sort()
      .join(','),
    [caseRequest?.categories]
  )

  const submitCoordinatorChange = (selection) => {
    if (!selection && selection !== 'none') return

    if (selection === 'none') {
      jwtPostFetcher(getJwt())(`/api/case/coordinator?requestId=${caseRequest.requestId}`,{},{method:"DELETE"})
        .then(() => {
          setCaseRequest(current => ({
            ...current,
            caseCoordinator: null,
          }))
          setNotification("Case coordinator removed successfully")
          coordinatorMenuToggle.close()
          setSearchQuery('')
          setSelectedCoordinator(null)
        })
        .catch(() => {
          setNotification({ type: "alert", msg: "Failed to remove case coordinator" })
        })
      return
    }

    const coordinator = selection
    jwtPostFetcher(getJwt())(`/api/case/coordinator/${coordinator.id}?requestId=${caseRequest.requestId}`,{},{method:"PUT"})
      .then(() => {
        setCaseRequest(current => ({
          ...current,
          caseCoordinator: {
            id: coordinator.id,
            name: `${coordinator.firstName} ${coordinator.lastName}`,
          }
        }))
        setNotification("Case coordinator updated successfully")
        coordinatorMenuToggle.close()
        setSearchQuery('')
        setSelectedCoordinator(null)
      })
      .catch(() => {
        setNotification({ type: "alert", msg: "Failed to update case coordinator" })
      })
  }

  const caseCoordinatorIsTheLoggedUser = caseRequest?.caseCoordinator && caseRequest?.caseCoordinator.id === userInfo.id
  const isOperator = userInfo?.role === OPERATOR
  const isManager = userInfo?.role !== OPERATOR

  useEffect(() => {
    if (!coordinatorMenuToggle.isOpen) {
      setSearchQuery('')
      setSelectedCoordinator(null)
      setHighlightedIndex(0)
    }
  }, [coordinatorMenuToggle.isOpen])

  useEffect(() => {
    if (!caseRequest?.requestId) {
      setCandidateCoordinators([])
      lastFetchedFetchKey.current = null
      return
    }

    const fetchKey = `${caseRequest.requestId}-${categoriesSignature}`
    if (lastFetchedFetchKey.current === fetchKey) {
      return
    }

    setCoordinatorError(null)
    lastFetchedFetchKey.current = fetchKey
    jwtGetFetcher(getJwt())(`/api/case/candidateCoordinators?requestId=${caseRequest.requestId}`)
      .then((response) => {
        setCandidateCoordinators(Array.isArray(response) ? response : [])
      })
      .catch((error) => {
        console.error('Error fetching candidate coordinators:', error);
        setCoordinatorError('Error loading users')
        setCandidateCoordinators([])
      })
  }, [caseRequest?.requestId, categoriesSignature])

  useEffect(() => {
    if (!coordinatorMenuToggle.isOpen) return
    const container = listContainerRef.current
    const target = itemRefs.current[highlightedIndex]
    if (!container || !target) return

    const containerRect = container.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()

    if (targetRect.top < containerRect.top) {
      container.scrollTop -= (containerRect.top - targetRect.top)
    } else if (targetRect.bottom > containerRect.bottom) {
      container.scrollTop += (targetRect.bottom - containerRect.bottom)
    }
  }, [highlightedIndex, coordinatorMenuToggle.isOpen])

  const filteredAdmins = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    return candidateCoordinators
      .filter(admin => admin?.status === 'ACTIVE')
      .filter(admin => {
        const name = `${admin?.firstName || ''} ${admin?.lastName || ''}`.trim().toLowerCase()
        return !query || name.includes(query)
      })
      .sort((a, b) => `${a?.firstName || ''} ${a?.lastName || ''}`.localeCompare(`${b?.firstName || ''} ${b?.lastName || ''}`))
      // .slice(0, 7)
  }, [candidateCoordinators, searchQuery])

  useEffect(() => {
    setSelectedCoordinator(null)
    setHighlightedIndex(0)
  }, [searchQuery])

  useEffect(() => {
    if (!coordinatorMenuToggle.isOpen) return
    const currentId = caseRequest?.caseCoordinator?.id
    if (!currentId) {
      setSelectedCoordinator(null)
      setHighlightedIndex(0)
      return
    }

    const match = filteredAdmins.find(admin => admin.id === currentId)
    if (match) {
      setSelectedCoordinator(match)
      const idx = filteredAdmins.findIndex(admin => admin.id === currentId)
      setHighlightedIndex(idx >= 0 ? idx + 1 : 0)
      return
    }

    // Fallback to a minimal coordinator object when not in the current filtered list
    setSelectedCoordinator({
      id: currentId,
      firstName: caseRequest?.caseCoordinator?.name || '',
      lastName: ''
    })
    setHighlightedIndex(0)
  }, [coordinatorMenuToggle.isOpen, caseRequest?.caseCoordinator, filteredAdmins])

  if (!(caseRequest.caseCoordinator?.name || isOperator || isManager)) {
    return null
  }

  return (
    <CaseCoordinator>
      <b>Coordinator: </b>
      {isManager ? (
        <span css="position: relative;">
          <CoordinatorLink onClick={coordinatorMenuToggle.toggle}>
            {caseRequest.caseCoordinator?.name ? (
              `${caseRequest.caseCoordinator?.name}${caseCoordinatorIsTheLoggedUser ? ' (you)' : ''}`
            ) : (
              'Not assigned'
            )}
            <Icon icon="arrow-down"/>
          </CoordinatorLink>
          {coordinatorMenuToggle.isOpen &&
            <ContextMenu 
              css="position: absolute; top: 100%; left: 0; z-index: 1000; max-height: 492px; min-width: 280px; padding: 0px; display: flex; flex-direction: column; overflow: hidden;"
            >
              <AssignCoordinatorHeader>
                <p>Assign a coordinator</p>
                <Icon icon="x" onClick={coordinatorMenuToggle.close}/>
              </AssignCoordinatorHeader>
              <div css="padding: 0px 16px; margin-top: 8px; margin-bottom: 8px; position: relative;">
                <SearchIcon icon="search"/>
                {searchQuery && searchQuery.length > 0 && <ClearSearchButton icon="x" 
                  onClick={(e) => {
                    setSearchQuery('')
                    e.stopPropagation()
                  }}
                />}
                <TextInput
                  raw
                  placeholder="Search for the person here"
                  value={searchQuery}
                  setValue={setSearchQuery}
                  autoFocus
                  css="padding-left: 32px; padding-right: 8px;"
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => {
                    e.stopPropagation()
                    const optionsCount = 1 + filteredAdmins.length
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                      e.preventDefault()
                      setHighlightedIndex(current => {
                        const base = Number.isFinite(current) ? current : 0
                        const delta = e.key === 'ArrowDown' ? 1 : -1
                        return (base + delta + optionsCount) % optionsCount
                      })
                      return
                    }
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      const selection = highlightedIndex === 0
                        ? 'none'
                        : filteredAdmins[highlightedIndex - 1]
                      if (selection) {
                        setSelectedCoordinator(selection)
                        submitCoordinatorChange(selection)
                      } else if (selectedCoordinator) {
                        submitCoordinatorChange(selectedCoordinator)
                      }
                    }
                  }}
                  onKeyUp={e => e.stopPropagation()}
                />
              </div>
              <div css="flex: 1 1 auto; overflow-y: auto; padding-bottom: 8px;" ref={listContainerRef}>
                <Li 
                  css={css`
                    ${contextMenuLinkPadding};
                    ${selectedCoordinator === 'none'
                      ? selectedCoordinatorMenuItemCss
                      : highlightedIndex === 0
                        ? highlightedCoordinatorMenuItemCss
                        : defaultCoordinatorMenuItemCss};
                  `}
                  label={
                    <span css="display: flex; align-items: center;"><i>
                      Not assigned
                      {selectedCoordinator === 'none' && (
                        <Icon icon="check" css="color: white; right: 10px; left: auto !important;"/>
                      )}
                      </i>
                    </span>
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedCoordinator('none')
                  }}
                  onMouseEnter={() => setHighlightedIndex(0)}
                  ref={el => { itemRefs.current[0] = el }}
                />
                {coordinatorError && (
                  <NoUsersDiv css="color: #d1434b;">
                    {coordinatorError}
                  </NoUsersDiv>
                )}
                {!coordinatorError && caseRequest?.requestId && filteredAdmins.length === 0 && (
                  <NoUsersDiv>
                    <SmileIcon icon="icon-sad" css="margin-right: 4px;"/><i>{searchQuery ? 'No person found for this search' : 'No person found'}</i>
                  </NoUsersDiv>
                )}
                {filteredAdmins.map((admin, idx) => (
                  <Li
                    css={css`
                      ${contextMenuLinkPadding};
                      ${selectedCoordinator?.id === admin.id
                        ? selectedCoordinatorMenuItemCss
                        : highlightedIndex === (idx + 1)
                          ? highlightedCoordinatorMenuItemCss
                          : defaultCoordinatorMenuItemCss};
                    `}
                    key={admin.id}
                    label={
                      <span 
                        css="display: flex; align-items: center;">
                        {`${admin.firstName} ${admin.lastName}`}
                        {selectedCoordinator?.id === admin.id && (
                          <Icon icon="check" css="color: white; right: 10px; left: auto !important;"/>
                        )}
                      </span>
                    }
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedCoordinator(admin)
                  }}
                  onMouseEnter={() => setHighlightedIndex(idx + 1)}
                  ref={el => { itemRefs.current[idx + 1] = el }}
                />
              ))}
              </div>
              <div css="padding: 16px 8px; background: #f2f2f2; flex: 0 0 auto;">
                <Button 
                  highlight 
                  disabled={!selectedCoordinator}
                  css="width: 100%;"
                  onClick={() => submitCoordinatorChange(selectedCoordinator)}
                >
                  Update coordinator
                </Button>
              </div>
            </ContextMenu>
          }
        </span>
      ) : isOperator ? (
        caseRequest.caseCoordinator?.name ? (
          caseCoordinatorIsTheLoggedUser ? (
            <span css="position: relative;">
              <span css="color: #007acc; cursor: pointer;" onClick={coordinatorMenuToggle.toggle}>
                {caseRequest.caseCoordinator.name} (you)
                <Icon icon="arrow-down" css="margin-left: 4px; font-size: 12px; color: #007acc;"/>
              </span>
              {coordinatorMenuToggle.isOpen &&
                <ContextMenu css="position: absolute; top: 100%; left: 0; z-index: 1000;">
                  <Li
                    css={contextMenuLinkPadding}
                    label={<i>Not assigned</i>} 
                    onClick={() => {
                      jwtPostFetcher(getJwt())(`/api/case/coordinator?requestId=${caseRequest.requestId}`,{},{method:"DELETE"})
                        .then(() => {
                          setCaseRequest(current => ({
                            ...current,
                            caseCoordinator: null,
                          }))
                          setNotification("Case coordinator removed successfully")
                          coordinatorMenuToggle.close()
                          setSearchQuery('')
                          setSelectedCoordinator(null)
                        })
                        .catch(() => {
                          setNotification({ type: "alert", msg: "Failed to remove case coordinator" })
                        })
                      coordinatorMenuToggle.close()
                    }}
                  />
                </ContextMenu>
              }
            </span>
          ) : (
            <span>{caseRequest.caseCoordinator?.name}</span>
          )
        ) : (
          <span css="position: relative;">
            <span css="color: #007acc; cursor: pointer;" onClick={coordinatorMenuToggle.toggle}>
              Not assigned
              <Icon icon="arrow-down" css="margin-left: 4px; font-size: 12px; color: #007acc;"/>
            </span>
            {coordinatorMenuToggle.isOpen &&
              <ContextMenu css="position: absolute; top: 100%; left: 0; z-index: 1000;">
                <Li 
                  css={contextMenuLinkPadding} 
                  label={<>{userInfo.firstName} {userInfo.lastName} <i>(you)</i></>}
                  onClick={() => {
                      jwtPostFetcher(getJwt())(`/api/case/coordinator/${userInfo.id}?requestId=${caseRequest.requestId}`,{},{method:"PUT"})
                        .then(() => {
                          setCaseRequest(current => ({
                            ...current,
                            caseCoordinator: {
                              id: userInfo.id,
                              name: `${userInfo.firstName} ${userInfo.lastName}`,
                            }
                          }))
                          setNotification("Case coordinator updated successfully")
                          coordinatorMenuToggle.close()
                          setSearchQuery('')
                          setSelectedCoordinator(null)
                        })
                        .catch(() => {
                          setNotification({ type: "alert", msg: "Failed to update case coordinator" })
                        })
                    coordinatorMenuToggle.close()
                  }}
                />
              </ContextMenu>
            }
          </span>
        )
      ) : (
        <span>{caseRequest.caseCoordinator?.name}{caseRequest.caseCoordinator?.name && caseRequest.caseCoordinator?.id && caseRequest.caseCoordinator?.id === userInfo.id && " (you)"}</span>
      )}   
    </CaseCoordinator>
  )
}
